// MinimizedDock — the parked-apps chips cluster for the shell PageSlot's
// minimize feature (2026-08-06 per Nika "we should support minimize feature
// for apps, to continue exactly where it left off").
//
// Renders one compact chip per `root._minimizedSlots` entry (icon + title +
// dismiss ✕). Clicking a chip RESTORES the app: it re-opens the route through
// the ONE opener (`el.call('openInSlot')` — shared/functions/openInSlot.js),
// which sets `root._canvasSlot`; the PageSlot's keep-alive host for that
// route (canvas/components/PageSlot.js `_mountPane`) then reveals the SAME
// mounted element — scroll, inputs and in-flight work intact. After a reload
// the hosts are gone (keep-alive is in-memory by nature) but the LIST is
// seeded back from per-workspace sessionStorage below, so the same chips
// offer COLD restores (fresh mount, best-effort continuation).
//
// Mounted INSIDE the PageSlot frame (canvas/components/PageSlot.js), not at
// the shell root: the frame is the feature's stacking context (1300) and is
// always mounted, the dock inherits its paint band without a new app.js
// mount, and declaring it BEFORE the panes keeps the phone full-bleed sheet
// painting ABOVE the chips with zero media hacks (same-context DOM order).
//
// Geometry: bottom-left, one step above the presence dock's baseline —
// OnlineMembersDock owns `left: A / bottom: X2` (the composer's own baseline,
// see its 2026-07-29 note), so the chips sit at `bottom: C` to clear that
// ~40px band with tokens only. The frame is pointerEvents:none; the dock
// re-enables interaction for itself, exactly like the panes do.
// Bare shared-library import, not a deep functions/minimizedSlots.js subpath
// — a per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js). Deferred call wrappers, not a module-scope
// destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const readMinimizedList = (...args) => _wsShared.functions.readMinimizedList(...args)
const removeMinimizedEntry = (...args) => _wsShared.functions.removeMinimizedEntry(...args)
const shortSlotTitle = (...args) => _wsShared.functions.shortSlotTitle(...args)
const writeMinimizedList = (...args) => _wsShared.functions.writeMinimizedList(...args)

// The workspace identity every consumer of the minimize feature shares —
// the AppAssistantThreadPicker resolution chain (workspace record → claims).
const _canvasWsIdOf = (root) =>
  root?.activeWorkspaceId || root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null

// Cold seeding: once per workspace per dock instance, when live state carries
// no entries (fresh boot OR the post-switch registry wipe), offer the
// persisted list for THAT workspace. Runs from onCreate + onUpdate — the
// `workspace`/`activeWorkspaceId` subscription re-fires it after a switch,
// and the `_seededWs` latch keeps it once-per-tenant so a user emptying the
// dock by hand doesn't get the stale storage list re-imposed.
const _seedFromStorage = (el, s) => {
  const root = s.root
  const wsId = _canvasWsIdOf(root)
  if (!wsId || el._seededWs === wsId) return
  el._seededWs = wsId
  const live = root?._minimizedSlots
  if (Array.isArray(live) && live.length) return
  const persisted = readMinimizedList(wsId)
  if (persisted.length && root && typeof root.update === 'function') {
    root.update({ _minimizedSlots: persisted })
  }
}

// One parked app. `childrenAs: 'state'` on the dock passes the minimized
// entry ({ href, title, icon, appKey?, wsId, minimizedAt }) as this chip's state.
export const MinimizedDockChip = {
  flow: 'x',
  align: 'center',
  gap: 'V',
  padding: 'X',
  round: 'radiusPill',
  theme: 'dialog',
  shadow: 'float',
  flexShrink: '0',
  minWidth: '0',
  maxWidth: 'G',
  AppCapsuleBtnToolbar: {
    compact: true,
    theme: 'transparent',
    minWidth: '0',
    gap: 'Z',
    'aria-label': (el, s) =>
      `${el.call('polyglot', 'canvas.minimizedDock.restore')} ${shortSlotTitle(el.call('storeItemText', { key: s.appKey, name: s.title || '' }, 'name')) || s.href || ''}`,
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      if (s.href) el.call('openInSlot', s.href, el)
    },
    Icon: { name: (el, s) => s.icon || 'layers', boxSize: 'iconSm' },
    Text: {
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) =>
        shortSlotTitle(el.call('storeItemText', { key: s.appKey, name: s.title || '' }, 'name')) ||
        s.href ||
        ''
    }
  },
  DismissBtn: {
    extends: 'PageSlotHeaderBtn',
    'aria-label': (el) => el.call('polyglot', 'canvas.minimizedDock.closeAria'),
    title: (el) => el.call('polyglot', 'canvas.minimizedDock.close'),
    Icon: { name: 'close' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      const next = removeMinimizedEntry(root?._minimizedSlots || [], s.href)
      writeMinimizedList(_canvasWsIdOf(root), next)
      if (root && typeof root.update === 'function') root.update({ _minimizedSlots: next })
    }
  }
}

export const MinimizedDock = {
  position: 'fixed',
  // BF-MINIMIZED-DOCK-OVER-RAIL-1 (repeat report, 2026-08-16): B6's
  // rail-parking branch is DELETED. Its justification — "the one band page
  // content provably never occupies" — was true of page content and false
  // of THE RAIL ITSELF: the band is empty precisely because the sidebar is
  // there, so the chips sat on the rail's own controls at every width (the
  // phone gate only hid the widest case). ONE placement now, the plain-
  // workspace right-edge stack, which already satisfied B6's brief ("move
  // them right", width never grows with count) and overlays nothing — the
  // right edge is content-inset territory on every surface.
  // Coordination (tasks-guy, 2026-08-16): ai is publishing an
  // --assistant-inset-right for the AI drawer — a fixed right-edge element
  // that ignores it lands UNDER the drawer, the same overlay class this
  // fix removes. Additive with a 0px fallback, so nothing changes until
  // ai's var ships.
  right: 'calc(var(--assistant-inset-right, 0px) + var(--spacing-A))',
  bottom: 'calc(var(--slot-bottom, var(--spacing-C)) + var(--spacing-Z))',
  pointerEvents: 'auto',
  flow: 'x',
  alignItems: 'center',
  gap: 'Z',
  maxWidth:
    'calc(100vw - var(--module-rail-clear, 0px) - var(--assistant-inset-right, 0px) - var(--spacing-B))',
  overflowX: 'auto',
  overscrollBehavior: 'contain',
  padding: 'Z',
  '@mobileL': { bottom: 'calc(env(safe-area-inset-bottom) + var(--spacing-A))' },
  // Tombstones — the deleted rail-parking branch (a stored payload would
  // otherwise resurrect the class block on merge-shaped surfaces; inert
  // here in the bundled shell, kept for grep-ability + the record).
  isDockInRail: null,
  '.isDockInRail': null,
  // Reactive `display:` gate (house keep-alive law — never `hide:` around
  // kept-alive surfaces; the dock follows the same grammar for consistency):
  // present in the tree always, painted only while something is parked.
  // Phone + open AI drawer: the drawer (min-width 320px) covers the whole
  // free band — at 500px it even overlaps the module rail — so there is no
  // legal place for chips; measured 2026-08-18: capped chips collapsed to
  // 18px slivers UNDER both surfaces. The dock hides for that combination
  // and returns the moment the drawer closes.
  display: (el, s) => {
    if (!(s.root?._minimizedSlots || []).length) return 'none'
    const phone = s.root?._isPhoneViewport
    const phoneNow =
      phone === true ||
      (phone == null &&
        typeof matchMedia === 'function' &&
        matchMedia('(max-width: 768px)').matches)
    if (phoneNow && s.root?.assistantOpen) return 'none'
    return 'flex'
  },
  // workspaceProject: the rail-column placement (isDockInRail) must
  // re-resolve when the module manifest lands after boot.
  subscribeTo: [
    '_minimizedSlots',
    'workspace',
    'activeWorkspaceId',
    'workspaceProject',
    '_isPhoneViewport',
    // phone×drawer display gate above — declared lists are the contract.
    'assistantOpen'
  ],
  onCreate: _seedFromStorage,
  onUpdate: _seedFromStorage,
  childrenAs: 'state',
  children: (el, s) => s.root?._minimizedSlots || [],
  childExtends: 'MinimizedDockChip'
}
